import { useEffect, useState } from "react";
import styles from "./ColorSwitcher.module.css";

const STORAGE_KEY = "idkf.theme"; // "light" | "dark"

/**
 * Light-first theme toggle.
 *
 * PRD §5: the product defaults to light when the user has no stored
 * preference. We deliberately do NOT follow prefers-color-scheme on first
 * visit: a beginner-friendly product should not debut in a dark palette
 * that reads like a betting app. Once the user chooses, we respect it.
 */
export function readStoredTheme() {
  try {
    const saved = window.localStorage.getItem(STORAGE_KEY);
    return saved === "light" || saved === "dark" ? saved : null;
  } catch {
    return null;
  }
}

export function applyTheme(theme) {
  document.documentElement.setAttribute("data-bs-theme", theme);
}

/** Called once at startup, before React renders, from src/index.js. */
export function initTheme() {
  const theme = readStoredTheme() ?? "light";
  applyTheme(theme);
  return theme;
}

const ColorSwitcher = ({ withLabel = false }) => {
  const [theme, setTheme] = useState(() => readStoredTheme() ?? "light");

  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  const toggleTheme = () => {
    const next = theme === "dark" ? "light" : "dark";
    setTheme(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* storage unavailable (private mode): theme still applies for this session */
    }
  };

  const isDark = theme === "dark";
  const label = isDark ? "Switch to light mode" : "Switch to dark mode";

  return (
    <button
      type="button"
      className={styles.button}
      onClick={toggleTheme}
      aria-label={label}
      title={label}
    >
      <i
        className={isDark ? "fa-solid fa-sun" : "fa-solid fa-moon"}
        aria-hidden="true"
      />
      {withLabel && <span className={styles.label}>{isDark ? "Light" : "Dark"}</span>}
    </button>
  );
};

export default ColorSwitcher;
